import { Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Select, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { $enum } from 'ts-enum-util';
import { IBrand } from '../../../main/entities';
import { transl } from '../../../main/transl';
import { mg } from '../../../mg/mg';
import { toasterSvc } from '../../../services/ToasterSvc';
import { useOmnistore } from '../../../stores/Omnistore';

const useStyles = makeStyles({
	editBrandDialog: {
		'& .MuiDialog-paper': {
			width: 400,
		},
	},
	elements: {
		margin: 10,
		display: 'grid',
		gridTemplateColumns: '2fr 3fr auto',
		gap: '10px',
	},
	delete: {
		color: '#F00',
	},
});

enum ENameSearchElementType {STRING='STRING', WORD_START='WORD_START', WORD='WORD'}

interface INameSearchElement {
	readonly type: ENameSearchElementType
	readonly string: string
}

interface IElementViewProps {
	readonly index: number
	readonly element: INameSearchElement
	setElement(index: number, element: INameSearchElement|null) : void
	readonly canDelete: boolean
}

const ElementView = observer(function(props: IElementViewProps) {
	const classes = useStyles();
	const {index, element, setElement, canDelete} = props;
	return <>
		<Select
			name="type"
			value={element.type}
			onChange={e => setElement(index, {...element, type: e.target.value as any})}
		>
			{$enum(ENameSearchElementType).map(s => <MenuItem value={s} key={s}>{transl(s)}</MenuItem>)}
		</Select>
		<TextField
			name="string"
			value={element.string}
			onChange={e => setElement(index, {...element, string: e.target.value})}
		/>
		<DeleteOutlineIcon
			color="disabled"
			className={canDelete ? classes.delete : ''}
			onClick={e => canDelete && setElement(index, null)}
		/>
	</>;
});

interface IEditBrandDialogProps {
	brand: IBrand
	onClose() : void
}

function getNameSearch(brand: IBrand | undefined) : Array<INameSearchElement> {
	return !brand ? [] : mg.parseJson(brand.nameSearchJson).elements;
}

export const EditBrandDialog = observer(function(props: IEditBrandDialogProps) {
	const classes = useStyles();
	const {onClose, brand} = props;
	const [elements, setElements] = useState(getNameSearch(brand));
	const omnistore = useOmnistore();
	const communicator = omnistore.communicator;
	function setElement(index: number, element: INameSearchElement|null) : void {
		const prefix = elements.slice(0, index);
		const e = element ? [element] : [];
		const suffix = elements.slice(index+1);
		setElements(prefix.concat(e, suffix));
	}
	function addElement() {
		setElements(elements.concat({type: ENameSearchElementType.WORD_START, string: brand.name}));
	}
	const save = () => {
		const nameSearchJson = ')]}\'\n' + mg.stringify({elements: elements});
		communicator.doPost('PutBrand', {...brand, nameSearchJson})
			.then(toasterSvc.handleResponse('saved'))
			.finally(onClose)
			.then(() => omnistore.multirepo.brandRepo.fetch('DIRECT'));
	};

	return <Dialog className={classes.editBrandDialog} open={true}>
		<DialogTitle>{transl('EditBrandDialog')}</DialogTitle>
		<DialogContent className={classes.elements}>
			{
				elements.map((e, i) => <ElementView key={i}
					index={i}
					element={e}
					setElement={setElement}
					canDelete={elements.length>1}
				/>)
			}
			<div/>
			<div/>
			<AddIcon
				onClick={addElement}
			/>
		</DialogContent>
		<DialogActions>
			<Button onClick={onClose}>{transl('cancel')}</Button>
			<Button color="primary" onClick={save}>{transl('save')}</Button>
		</DialogActions>
	</Dialog>;
});
